import type React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { ResultsAPI } from "generated/types";
import type { ProductModules } from "models/product/ProductDetails";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";

import { trackPlatformEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";

import { useMemo } from "react";
import { CssIcon } from "styles/helpers/layout";
import { BaseModuleCard } from "../BaseModuleCard/BaseModuleCard";
import {
  StyledSectionProgressList,
  StyledSectionProgressListItem,
} from "./WritingModuleCard.styled";

interface WritingModuleCardProps {
  productId: number;
  moduleDetails: ProductModules;
  lastPracticedTopicStatus?: ResultsAPI.LastPracticedWritingTopicStatus | null;
  writingProgressPerModuleSection?: ResultsAPI.WritingSectionProgress[] | null;
}

export const WritingModuleCard: React.FC<WritingModuleCardProps> = ({
  productId,
  moduleDetails: module,
  lastPracticedTopicStatus = null,
  writingProgressPerModuleSection = null,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("homepage");

  const userHasAlreadyPracticed = !!lastPracticedTopicStatus;

  // Calculate average progress in all sections
  const avgProgress = useMemo(() => {
    if (!writingProgressPerModuleSection || writingProgressPerModuleSection.length === 0) {
      return 0;
    }

    return (
      writingProgressPerModuleSection.reduce((total, { progress }) => total + progress, 0) /
      writingProgressPerModuleSection.length
    );
  }, [writingProgressPerModuleSection]);

  const userHasAlreadyFinished = avgProgress >= 100;

  // Render list of elements with progress per section (if available)
  const ProgressPerSectionList = useMemo(() => {
    // Filter out items with null section (meaning it contains progress on ungrouped subjects)
    const progressPerSection = writingProgressPerModuleSection?.filter(
      ({ moduleSectionId }) => !!moduleSectionId,
    );

    if (!progressPerSection?.length) {
      return null;
    }

    return (
      <StyledSectionProgressList>
        {progressPerSection.map(({ moduleSectionId, moduleSectionTitle, progress }) => {
          const isCompleted = progress >= 100;
          return (
            <StyledSectionProgressListItem
              key={moduleSectionId}
              data-cy="section-list-item"
              $isCompleted={isCompleted}
            >
              <CssIcon
                data-cy="section-status"
                iconName={!isCompleted ? "far fa-circle" : "far fa-check-circle"}
                iconSize="lg"
              />
              <span data-cy="section-name">{moduleSectionTitle}</span>
              <span data-cy="section-progress">{`${Math.round(progress)}%`}</span>
            </StyledSectionProgressListItem>
          );
        })}
      </StyledSectionProgressList>
    );
  }, [writingProgressPerModuleSection]);

  const onCardClick = () => {
    if (!userHasAlreadyPracticed || userHasAlreadyFinished) {
      navigate(
        buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS, {
          productId,
          moduleId: module.id,
        }),
      );
      return;
    }

    const { topicId, subjectId } = lastPracticedTopicStatus;

    // Track continue event in Metabase
    trackPlatformEvent({
      module: STUDENT_MODULES.DASHBOARD,
      itemId: PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_CONTINUE_PRACTICING_TOPIC,
      value: JSON.stringify({
        productId,
        moduleId: module.id,
        subjectId,
        topicId,
      }),
    });

    // Redirect to writing topic details page
    navigate(
      buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_TOPIC_DETAILS, {
        productId,
        moduleId: module.id,
        subjectId,
        topicId,
      }),
    );
  };

  return (
    <BaseModuleCard
      moduleId={module.id}
      moduleTitle={module.title}
      moduleDescription={module.description || ""}
      actionButtonLabel={
        userHasAlreadyFinished
          ? t("studentDashboard.currentProgressSection.moduleCard.reviewButton")
          : userHasAlreadyPracticed
            ? t("studentDashboard.currentProgressSection.moduleCard.continueButton")
            : t("studentDashboard.currentProgressSection.moduleCard.startButton")
      }
      onCardClick={onCardClick}
      actionButtonVariant={userHasAlreadyFinished ? "success" : "primary"}
      onActionButtonClick={onCardClick}
      avgProgress={Math.ceil(avgProgress)}
      extraCardContent={ProgressPerSectionList}
    />
  );
};
